import { Group, Paper, Stack } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import type { LiteWorkspace } from '@repo/api-codegen';
import { useApiSuperuserGetWorkspace } from '@repo/api-codegen';
import { Text } from '@repo/foundations';
import { useEffect, useState } from 'react';
import { WorkspaceLogo } from '../../../../components/SecodaAppShell/SideBar/WorkspaceSelector/WorkspaceLogo';

interface IWorkspaceSearchProps {
	searchTerm: string;
	onChange: (workspace: LiteWorkspace) => void;
}

function WorkspaceSearch({ searchTerm, onChange }: IWorkspaceSearchProps) {
	const [debouncedValue] = useDebouncedValue(searchTerm, 300);
	const [filteredWorkspaces, setFilteredWorkspaces] = useState<LiteWorkspace[]>(
		[]
	);

	const { data: workspaces = [] } = useApiSuperuserGetWorkspace(
		{
			queryParams: {
				search_term: debouncedValue,
			},
		},
		{
			enabled: debouncedValue.length > 0,
		}
	);

	// Filter for only customer workspaces and trial workspaces
	// This is a client-side filter in case the API doesn't provide this filtering
	useEffect(() => {
		if (!workspaces.length) {
			setFilteredWorkspaces([]);
			return;
		}

		// In a real implementation, we would need to get information about which workspaces are customers
		// or in trial. For now, we'll just use all workspaces from the API.
		setFilteredWorkspaces(workspaces);
	}, [workspaces]);

	if (!debouncedValue || debouncedValue.length < 2) {
		return null;
	}

	return (
		<Paper shadow="sm" p="md" withBorder>
			<Stack spacing="xs">
				<Text size="sm" weight="bold">
					Search Results
				</Text>
				{filteredWorkspaces.length === 0 ? (
					<Text size="sm" color="text/secondary/default">
						No workspaces found
					</Text>
				) : (
					filteredWorkspaces.map((workspace) => (
						<Paper
							key={workspace.id}
							p="xs"
							sx={(theme) => ({
								cursor: 'pointer',
								'&:hover': {
									backgroundColor: theme.other.getColor(
										'surface/primary/hover'
									),
								},
							})}
							onClick={() => onChange(workspace)}
						>
							<Group>
								<WorkspaceLogo
									logo={workspace.logo || '/images/logos/cfsecoda.webp'}
									size={24}
								/>
								<Stack spacing={0}>
									<Text size="sm" weight="bold">
										{workspace.name}
									</Text>
									<Text size="xs" color="text/secondary/default">
										{workspace.id}
									</Text>
								</Stack>
							</Group>
						</Paper>
					))
				)}
			</Stack>
		</Paper>
	);
}

export default WorkspaceSearch;
